<template>
  <div>
    <div v-if="!prescription">
      <div class="tw-text-center tw-text-gray-500 tw-py-6">Loading prescription details...</div>
    </div>
    <div v-else>
      <h2
        class="tw-text-xl tw-text-primary-500 tw-bg-transparent tw-shadow-none tw-font-sans tw-inline-flex tw-items-center border-0 focus:tw-outline-none focus:tw-text-primary-700 tw-mb-4"
      >
        Patient's Prescription
      </h2>
      <div class="tw-flex tw-justify-between tw-mb-5">
        <div class="tw-flex tw-flex-col">
          <div class="tw-flex">
            <div class="tw-text-gray-800 tw-font-normal">{{ prescription.businessProgram.name }}</div>
            <base-badge class="tw-ml-2 tw-my-auto" :color="statusColor(status)" size="sm">{{ getPrescriptionStatus(status) }}</base-badge>
            <base-badge v-if="prescription.isMerge" class="tw-ml-2" size="sm" color="gray">Merged</base-badge>
          </div>
          <div class="tw-text-gray-600 tw-text-sm tw-mt-1">{{ getLocation(prescription) }}</div>
          <div class="tw-flex-col tw-mt-2">
            <div class="tw-text-xs tw-font-medium tw-text-gray-800">Submitted</div>
            <div class="tw-my-auto tw-text-sm tw-text-gray-600">{{ formatDate(prescription.submittedDate) }}</div>
          </div>
        </div>
      </div>
      <div>
        <div class="tw-pb-2">
          <div v-if="medications.length === 0" class="tw-text-sm tw-text-red-600 tw-mb-3">No medications available for this activity.</div>
          <div v-for="(medication, index) in medications" :key="index" class="tw-mb-3">
            <div class="tw-text-xs tw-font-medium tw-text-gray-800">{{ medicationLabel + " " + (startingMedicationIndex + index) }}</div>
            <div class="tw-text-sm tw-text-gray-600">
              {{ medication.businessItemCategory.name.value || medication.name || "Unnamed Medication" }}
            </div>
          </div>
        </div>
        <div class="tw-pb-2">
          <div v-for="group in groupedRxItems" :key="group.productGroup">
            <div v-for="(rxItem, idx) in group.items" :key="rxItem.id || rxItem.rxNumber || `${group.productGroup}-${idx}`" class="tw-mb-3">
              <div class="tw-text-xs tw-font-medium tw-text-gray-800">
                {{ group.productGroup }}
                <template v-if="group.items.length > 1">
                  {{ idx + 1 }}</template
                >
              </div>
              <div class="tw-text-sm tw-text-gray-600" :class="{ 'tw-text-red-600': group.productGroup.toLowerCase().includes('clinical justification') }">
                {{ rxItem.businessItemCategory.name.value || rxItem.businessItemCategory.name }}
              </div>
            </div>
          </div>
        </div>
        <div v-if="supplies.length" class="tw-pb-2">
          <div v-for="group in groupedSupplies" :key="group.productGroup">
            <div v-for="(rxItem, idx) in group.items" :key="rxItem.id || `${group.productGroup}-${idx}`" class="tw-mb-3">
              <div class="tw-text-xs tw-font-medium tw-text-gray-800">
                {{ group.productGroup
                }}<template v-if="group.items.length > 1">
                  {{ idx + 1 }}</template
                >
              </div>
              <div class="tw-text-sm tw-text-gray-600">{{ rxItem.name.value || "" }}</div>
            </div>
          </div>
        </div>
        <div class="tw-w-full tw-border-gray-200 tw-border-t"></div>
        <div class="tw-mt-8">
          <div class="tw-flex tw-items-center tw-justify-between">
            <div class="tw-text-gray-800 tw-font-normal">Notes</div>
          </div>
          <div class="tw-mt-2 tw-overflow-auto" style="max-height: 200px">
            <div v-for="note in prescription.notes" :key="note.id" class="tw-flex-col tw-mb-3">
              <div class="tw-flex">
                <div class="tw-text-gray-500 tw-text-xs tw-font-semibold">{{ note.insertByName }}</div>
                <div class="tw-text-gray-500 tw-text-xs tw-ml-2">{{ formatDate(note.insertDate) }}</div>
              </div>
              <div class="tw-text-gray-800 tw-text-sm">{{ note.noteText }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { capitalize, orderBy, groupBy } from "lodash";
import { mapGetters } from "vuex";
import moment from "moment";
import { PRESCRIPTION_STATUS_OPTIONS } from "@/constants/constants";

export default {
  components: {},
  props: {
    businessId: String,
    userId: String,
    prescription: Object,
    allRxItems: Array,
    todoLabel: String,
    todoItem: Object,
  },
  emits: ["medications-resolved"],

  data() {
    return {
      capitalize,
      medications: [],
      rxItems: [],
      supplies: [],
      medicationLabel: "Medication",
      startingMedicationIndex: 1,
    };
  },
  computed: {
    ...mapGetters("location", ["locations"]),
    orderedMedications() {
      return this.prescription && this.prescription.medications ? orderBy(this.prescription.medications, [(x) => x.sortOrder, (x) => x.businessItemCategory.name.value]) : [];
    },
    status() {
      return this.prescription ? (this.prescription.isDeleted ? "deleted" : this.prescription.status.value) : "";
    },
    extractedMonths() {
      if (!this.todoLabel) return [];
      const months = this.extractMonthsFromLabel(this.todoLabel);
      return months || [];
    },
    hasMedications() {
      return this.medications.length > 0;
    },
    groupedRxItems() {
      if (!this.rxItems || this.rxItems.length === 0) {
        return [];
      }
      const filteredItems = this.rxItems.filter(
        (item) => item.businessItemCategory && item.businessItemCategory.productGroup && item.businessItemCategory.productGroup.toLowerCase().includes("clinical justification"),
      );

      const grouped = groupBy(filteredItems, (item) => (item.businessItemCategory ? item.businessItemCategory.productGroup : "Other Rx Items"));

      return Object.keys(grouped)
        .map((groupName) => {
          const itemsInGroup = grouped[groupName];
          const sortedItemsInGroup = orderBy(itemsInGroup, [(x) => x.sortOrder, (x) => x.businessItemCategory && x.businessItemCategory.name && x.businessItemCategory.name.value]);
          return {
            productGroup: groupName,
            items: sortedItemsInGroup,
          };
        })
        .sort((a, b) => a.productGroup.localeCompare(b.productGroup));
    },
    groupedSupplies() {
      if (!this.supplies || this.supplies.length === 0) return [];

      const grouped = groupBy(this.supplies, function(item) {
        return item.productGroup || "Other Items";
      });
      return Object.keys(grouped)
        .map(function(groupName) {
          const items = grouped[groupName].filter(function(item) {
            return !!item;
          });
          return {
            productGroup: groupName,
            items: orderBy(items, [
              function(item) {
                return item.sortOrder !== undefined && item.sortOrder !== null ? item.sortOrder : 0;
              },
              function(item) {
                return item.name.value || "";
              },
            ]),
          };
        })
        .sort(function(a, b) {
          return a.productGroup.localeCompare(b.productGroup);
        });
    },
  },
  watch: {
    prescription: {
      immediate: true,
      handler() {
        this.updateMedication();
        if (this.prescription && this.prescription.rxItems) {
          this.rxItems = this.prescription.rxItems;
        } else {
          this.rxItems = [];
        }
      },
    },
    todoLabel: {
      immediate: true,
      handler() {
        this.updateMedication();
      },
    },
    hasMedications: {
      immediate: true,
      handler(val) {
        this.$emit("medications-resolved", val);
      },
    },
  },
  methods: {
    extractMonthsFromLabel(label) {
      if (!label) return null;
      let match = label.match(/Months?\s*#?(\d+)\s*&\s*(\d+)/i);
      if (match) return [parseInt(match[1]), parseInt(match[2])];
      match = label.match(/Month\s*#?(\d+)/i);
      if (match) return [parseInt(match[1])];
      match = label.match(/#\s*(\d+)/i);
      if (match) return [parseInt(match[1])];
      return null;
    },
    getMedications(prescription, todoLabel = null) {
      if (!prescription || !Array.isArray(prescription.medications)) return [];
      const meds = prescription.medications;
      const programFieldLabel = prescription.businessProgram && prescription.businessProgram.programFieldLabel;
      const hasMonthLabel = programFieldLabel && /month/i.test(programFieldLabel);
      if (!hasMonthLabel || !todoLabel) {
        return meds;
      }

      const months = this.extractMonthsFromLabel(todoLabel);
      if (!months || months.length === 0) {
        const todoLabelsFor1month = ["submit refill auth to pharmacy b12", "submit refill auth to pharmacy wlsemb12"];

        //For refill activities, show only the medications for month 1 and 2
        if (todoLabelsFor1month.includes(todoLabel.toLowerCase()) && hasMonthLabel) {
          return meds.length >= 1 ? meds.slice(0, 1) : meds;
        } else if (todoLabel.toLowerCase().includes("submit refill") && hasMonthLabel) {
          return meds.length >= 2 ? meds.slice(0, 2) : meds;
        }
        return meds;
      }

      // Select meds matching requested months
      const selected = months
        .map((m) => meds[m - 1]) // zero-based
        .filter((med) => med !== undefined);

      // If no matches found, show only the last medication (if any exist)
      // if (selected.length === 0 && meds.length > 0) {
      //   return [meds[meds.length - 1]];
      // }
      return selected;
    },
    updateMedication() {
      if (this.prescription) {
        this.medications = this.getMedications(this.prescription, this.todoLabel);
        const programFieldLabel = this.prescription.businessProgram && this.prescription.businessProgram.programFieldLabel;
        this.medicationLabel = programFieldLabel ? this.capitalize(programFieldLabel) : "Medication";
        // if label is Medication then find medication from activityDefinedMedicationIds if exists or not null
        const activityDefinedMedicationIds = this.todoItem && this.todoItem.type ? this.todoItem.type.medicationIds : null;

        if (this.medicationLabel === "Medication" && activityDefinedMedicationIds && activityDefinedMedicationIds.trim() !== "" && activityDefinedMedicationIds.trim() !== "[]") {
          try {
            const definedMedicationIds = JSON.parse(activityDefinedMedicationIds);
            if (definedMedicationIds.length > 0) {
              this.medications = this.prescription.medications.filter((med) => definedMedicationIds.includes(String(med.businessItemCategory.id)));
            }
          } catch (e) {
            console.error("Failed to parse activityDefinedMedicationIds JSON:", e);
          }
        } else if (this.medicationLabel === "Month") {
          const months = this.extractedMonths;
          if (months && months.length > 0) {
            this.startingMedicationIndex = months[0];
          }
        }
      } else {
        this.medications = [];
      }

      if (this.prescription && this.prescription.rxItems) {
        this.rxItems = this.prescription.rxItems;
      }

      if (this.todoItem && this.todoItem.type) {
        //first check if we have conditional items in activity type
        if (this.todoItem.type.conditionalItems && JSON.parse(this.todoItem.type.conditionalItems).length > 0) {
          let conditionals = [];
          try {
            if (typeof this.todoItem.type.conditionalItems === "string") {
              conditionals = JSON.parse(this.todoItem.type.conditionalItems);
            } else if (Array.isArray(this.todoItem.type.conditionalItems)) {
              conditionals = this.todoItem.type.conditionalItems;
            }
          } catch (e) {
            conditionals = [];
          }

          let matchedRxItemIds = new Set();
          for (let conditional of conditionals) {
            if (!conditional || !conditional.SigCode || !Array.isArray(conditional.RxItemIds)) continue;

            // Check for sigCode match in any medication
            const matchFound = this.medications.some((med) => {
              const shortDesc = med.businessItemCategory.description.short || "";
              const longDesc = med.businessItemCategory.description.long || "";
              return shortDesc.toLowerCase().includes(conditional.SigCode.toLowerCase()) || longDesc.toLowerCase().includes(conditional.SigCode.toLowerCase());
            });

            if (matchFound) {
              // Add all rxItemIds to the set
              conditional.RxItemIds.forEach((id) => matchedRxItemIds.add(id));
            }
          }
          if (matchedRxItemIds.size > 0) {
            this.supplies = this.allRxItems.filter((rx) => matchedRxItemIds.has(rx.id));
          } else {
            this.supplies = [];
          }
        }

        if (this.supplies.length === 0) {
          //use default rx items set on activity type
          if (
            this.todoItem.type.businessItemCategoryIds &&
            this.todoItem.type.businessItemCategoryIds.trim() !== "" &&
            this.todoItem.type.businessItemCategoryIds.trim() !== "[]"
          ) {
            try {
              const definedRxItemIds = JSON.parse(this.todoItem.type.businessItemCategoryIds);
              this.supplies = this.allRxItems.filter((rx) => definedRxItemIds.includes(String(rx.id)));
            } catch (e) {
              console.error("Failed to parse defaultRxItemIds JSON:", e);
            }
          }
        }
      }
    },
    formatDate(date) {
      return new moment.utc(date).local().format("MM/DD/YYYY hh:mm a");
    },

    getLocation(prescription) {
      const location = this.locations.find((l) => l.businessLocationId === prescription.businessLocationId);
      return location ? location.name : "";
    },

    getPrescriptionStatus(status) {
      const found = PRESCRIPTION_STATUS_OPTIONS.find((s) => s.value === status);
      return found ? found.label : "Unknown";
    },

    statusColor(status) {
      switch ((status || "").toLowerCase()) {
        case "deleted":
        case "expired":
          return "gray";
        default:
          return "green";
      }
    },
  },
};
</script>
